import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from '../store/Store'
import {clientApi} from './Client.api'
import {IClientDto} from './Client.model'
import { IFilterDto, TSort } from '../../common/model/Core.model'
import { signOut } from '../auth/Auth.slice'

type InitialState = {
  filtersSelection: IFilterDto[]
  clients: {
    error?: string
    loading: boolean
    data: IClientDto[]
    pagination?: {
      limit?: number
      sort?: TSort
      page?: string | undefined
    }
    filters?: IFilterDto[]
  }
  client: {
    error?: string
    loading: boolean
    data?: IClientDto
  }
}
const initialState = {
  filtersSelection: [],
  clients: {
    error: undefined,
    loading: false,
    data: [],
    pagination: {
      limit: 25,
      sort: 'ASC',
      page: undefined,
    },
    filters: undefined,
  },
  client: {
    error: undefined,
    loading: false,
    data: undefined,
  },
} as InitialState

export const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    setClientFiltersSelection: (state, action: PayloadAction<IFilterDto[]>) => {
      state.filtersSelection = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(signOut, () => initialState)
      //Search
      .addMatcher(clientApi.endpoints.clientSearch.matchPending, (state) => {
        return {
          ...state,
          clients: {
            ...initialState.clients,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientSearch.matchFulfilled, (state, action) => {
        return {
          ...state,
          clients: {
            ...state.clients,
            error: undefined,
            loading: false,
            data: action.payload.data,
            pagination: action.payload.pagination,
            filters: action.payload.filters,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientSearch.matchRejected, (state, action) => {
        return {
          ...state,
          clients: {
            ...state.clients,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //LoadMore
      .addMatcher(clientApi.endpoints.clientLoadMore.matchPending, (state) => {
        return {
          ...state,
          clients: {
            ...state.clients,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientLoadMore.matchFulfilled, (state, action) => {
        return {
          ...state,
          clients: {
            ...state.clients,
            error: undefined,
            loading: false,
            data: state.clients.data.concat(action.payload.data),
            pagination: action.payload.pagination,
            filters: action.payload.filters,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientLoadMore.matchRejected, (state, action) => {
        return {
          ...state,
          clients: {
            ...state.clients,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //GetById
      .addMatcher(clientApi.endpoints.clientGetById.matchPending, (state) => {
        return {
          ...state,
          client: {
            ...initialState.client,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientGetById.matchFulfilled, (state, action) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: undefined,
            loading: false,
            data: action.payload,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientGetById.matchRejected, (state, action) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //Create
      .addMatcher(clientApi.endpoints.clientCreate.matchPending, (state) => {
        return {
          ...state,
          client: {
            ...initialState.client,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientCreate.matchFulfilled, (state, action) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: undefined,
            loading: false,
            data: action.payload,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientCreate.matchRejected, (state, action) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //DeleteById
      .addMatcher(clientApi.endpoints.clientDeleteById.matchPending, (state) => {
        return {
          ...state,
          client: {
            ...initialState.client,
            error: undefined,
            loading: true,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientDeleteById.matchFulfilled, (state, action) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: undefined,
            loading: false,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientDeleteById.matchRejected, (state, action) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: action.error.message,
            loading: false,
          },
        }
      })
      //UpdateById
      .addMatcher(clientApi.endpoints.clientUpdateById.matchPending, (state) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: undefined,
            loading: true
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientUpdateById.matchFulfilled, (state, action) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: undefined,
            loading: false,
            data: action.payload,
          },
        }
      })
      .addMatcher(clientApi.endpoints.clientUpdateById.matchRejected, (state, action) => {
        return {
          ...state,
          client: {
            ...state.client,
            error: action.error.message,
            loading: false,
          },
        }
      })
  },
})

export const {setClientFiltersSelection} = clientSlice.actions
export default clientSlice.reducer
export const selectClientsError = (state: RootState) => state.clientReducer.clients.error
export const selectClientsLoading = (state: RootState) => state.clientReducer.clients.loading
export const selectClients = (state: RootState) => state.clientReducer.clients.data
export const selectClientsPagination = (state: RootState) => state.clientReducer.clients.pagination
export const selectClientsFilters = (state: RootState) => state.clientReducer.clients.filters
export const selectClientFiltersSelection = (state: RootState) => state.clientReducer.filtersSelection

export const selectClientError = (state: RootState) => state.clientReducer.client.error
export const selectClientLoading = (state: RootState) => state.clientReducer.client.loading
export const selectClient = (state: RootState) => state.clientReducer.client.data
